﻿import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CONFIG } from '../core/config';
import { catchError, finalize } from 'rxjs/operators';
import { ExceptionService } from '../core/services/exception.service';
import { PagedResultModel } from '../core/models/paged-result.model';
import { SpinnerService } from '../core/spinner/spinner.service';
import { SurveySearch } from '../core/models/surveys/survey-search';
import { Survey } from '../core/models/surveys/survey';
import { Question } from '../core/models/surveys/question';
import { ProposedAnswer } from '../core/models/surveys/proposed-answer';
import { ParticipantAnswerModel } from '../core/models/surveys/participant-answer-model';
import { DropdownItem } from '../core/models/dropdown-item';
import { ParticipantDetails } from '../core/models/surveys/participant-details';
import { SurveyDetails } from '../core/models/surveys/survey-details';
import * as CryptoJS from 'crypto-js';
@Injectable({
    providedIn: 'root'
})
export class SurveysService {
    
    private secretKey = 'my-secret-key';
   
    constructor(private _http: HttpClient,
        private _exceptionService: ExceptionService,
        private _spinnerService: SpinnerService) { }
    saveSurveyParticipantID(participantID: string): void {
        localStorage.setItem('surveyParticipantID', this.encryptData(participantID));
    }
    getSurveyParticipantID(): string | null {
        if (localStorage.getItem('surveyParticipantID')) {

            return this.decryptData(localStorage.getItem('surveyParticipantID'));
        }
        else return null;
    }
    removeSurveyParticipantIDFromLocalStorage(): void {
        localStorage.removeItem('surveyParticipantID');
    }
    encryptData(data: any): string {
        const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(data), this.secretKey).toString();
        return encryptedData;
    }

    decryptData(encryptedData: string): any {
        const decryptedData = CryptoJS.AES.decrypt(encryptedData, this.secretKey).toString(CryptoJS.enc.Utf8);
        return JSON.parse(decryptedData);
    }

    searchSurveys(query: SurveySearch): Observable<PagedResultModel> {
        let headers = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };
        this._spinnerService.show();
        return this._http.post<PagedResultModel>(CONFIG.baseUrls.surveys.search, query, headers)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    getSurveyByID(id: string, participantId, langID: number): Observable<Survey> {
        this._spinnerService.show();
        return this._http.get<Survey>(CONFIG.baseUrls.surveys.getById + id + "/" + participantId + "/" + langID)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    } 
    getSurveyBySurveyID(id: number, langID: number): Observable<SurveyDetails> {
        this._spinnerService.show();
        return this._http.get<SurveyDetails>(CONFIG.baseUrls.surveys.getSurveyBySurveyId + id + "/" + langID)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    getQuestionByID(id: number, langID: number, ordNum: number): Observable<Question> {
        this._spinnerService.show();
        return this._http.get<Question>(CONFIG.baseUrls.surveys.getQuestionById + id + "/" + langID + "/" + ordNum)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getProposedAnswersByQuestionId(query: any): Observable<ProposedAnswer[]> {
        let headers = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };
        this._spinnerService.show();
        return this._http.post<PagedResultModel>(CONFIG.baseUrls.surveys.getProposedAnswersByQuestionID, query, headers)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getProposedAnswerOptionsByQuestionId(query: any): Observable<PagedResultModel> {
        let headers = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };
        this._spinnerService.show();
        return this._http.post<PagedResultModel>(CONFIG.baseUrls.surveys.getProposedAnswerOptionsByQuestionID, query, headers)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    getParticipantAnswerByQuestionID(questionId: number, surveyId: number, surveyParticipantID: number, languageID: number): Observable<any> {
        let headers = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };
        this._spinnerService.show();
        return this._http.post<PagedResultModel>(CONFIG.baseUrls.surveys.getParticipantAnswerByQuestionID, { "questionID": questionId, "surveyID": surveyId, "languageID": languageID, "surveyParticipantID": surveyParticipantID }, headers)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    saveAnswers(answers: ParticipantAnswerModel): Observable<boolean> {
        let headers = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };


        this._spinnerService.show();

        return this._http.post<boolean>(CONFIG.baseUrls.surveys.saveParticipantAnswers, answers, headers)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    } saveParticipantDetails(details: ParticipantDetails): Observable<boolean> {
        let headers = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };


        this._spinnerService.show();

        return this._http.post<boolean>(CONFIG.baseUrls.surveys.saveParticipantDetails, details, headers)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    sendCode(phoneNum: string, surveyID, masterSurveyID): Observable<any> {
        let headers = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };

        this._spinnerService.show();

        return this._http.post<any>(CONFIG.baseUrls.surveys.sendSms, { 'phoneNum': phoneNum, 'surveyID': surveyID, 'masterSurveyID': masterSurveyID }, headers)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    checkCode(phoneNum: string, surveyID: number, code: string,): Observable<number> {
        let headers = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };

        this._spinnerService.show();

        return this._http.post<boolean>(CONFIG.baseUrls.surveys.checkCode, { 'phoneNum': phoneNum, 'surveyID': surveyID, 'code': code }, headers)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    getAllSexesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllGendersForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    getAllAgeStructuresForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllAgeStructuresForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllTerritoriesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllTerritoriesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllUnenploymentPeriodForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllUnenploymentPeriodForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllMasterSurveySensitiveCategoriesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllMasterSurveySensitiveCategoriesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    addSurveyParticipant(SurveyUniqueIdentifier: string, userId: number): Observable<number> {
        let headers = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };
        this._spinnerService.show();
        return this._http.post<number>(CONFIG.baseUrls.surveys.addSurveyParticipant, { 'surveyUniqueIdentifier': SurveyUniqueIdentifier, 'userId': userId }, headers)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    searchAllSurveys(query: SurveySearch): Observable<PagedResultModel> {
        let headers = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };
        this._spinnerService.show();
        return this._http.post<PagedResultModel>(CONFIG.baseUrls.surveys.searchAll, query, headers)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
}